<script setup>
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Badge, Button, EmptyState, FormField, Input, Modal, PageHeader, Pagination, Section, Select, Spinner, Table, Toolbar, useToast } from '@drama/ui'
import { loadManagedGames, setManagedGameListing } from '../lib/index.js'

const api = inject('api')
if (!api) throw new Error('GameManagement: api provider is required')
const { t, locale } = useI18n()
const toast = useToast()
const text = (key, params = {}) => t(`visualGameAdmin.gameManagement.${key}`, params)
const items = ref([])
const loading = ref(false)
const error = ref('')
const query = ref('')
const game_type = ref('')
const listing = ref('')
const page = ref(1)
const page_size = 20
const target = ref(null)
const saving = ref(false)
const action_error = ref('')
const type_options = computed(() => [{ value: '', label: text('allTypes') }, ...['story', 'scenario'].map(value => ({ value, label: t(`gameCatalog.types.${value}`) }))])
const status_options = computed(() => [{ value: '', label: text('allStatuses') }, ...['listed', 'unlisted'].map(value => ({ value, label: text(value) }))])
const filtered = computed(() => {
  const needle = query.value.trim().toLocaleLowerCase()
  return items.value.filter(item => (!game_type.value || item.game_type === game_type.value)
    && (!listing.value || item.listed === (listing.value === 'listed'))
    && (!needle || [item.title, item.id, item.slug, item.owner_email, item.owner_id].some(value => String(value ?? '').toLocaleLowerCase().includes(needle))))
})
const pages = computed(() => Math.max(1, Math.ceil(filtered.value.length / page_size)))
const rows = computed(() => filtered.value.slice((page.value - 1) * page_size, page.value * page_size))
const columns = computed(() => [
  { key: 'title', label: text('game'), width: '360px', wrap: true },
  { key: 'game_type', label: text('type'), width: '120px' },
  { key: 'listed', label: text('status'), width: '110px' },
  { key: 'updated_at', label: text('updatedAt'), width: '170px' },
  { key: 'actions', label: t('visualGameAdmin.common.actions'), width: '240px', wrap: true, align: 'right' },
])
watch([query, game_type, listing], () => { page.value = 1 })
watch(pages, value => { page.value = Math.min(page.value, value) })
onMounted(load)
async function load() {
  loading.value = true
  error.value = ''
  try { items.value = await loadManagedGames(api) }
  catch (failure) { error.value = failure.message }
  finally { loading.value = false }
}
function reset() { query.value = ''; game_type.value = ''; listing.value = ''; page.value = 1 }
function ask(item) { target.value = item; action_error.value = '' }
function close() { if (!saving.value) target.value = null }
async function save() {
  if (saving.value || !target.value) return
  saving.value = true
  action_error.value = ''
  try {
    const item = target.value
    await setManagedGameListing(api, item, !item.listed)
    toast.success(text(item.listed ? 'unlistedSuccess' : 'listedSuccess', { title: item.title }))
    target.value = null
    await load()
  } catch (failure) { action_error.value = failure.message }
  finally { saving.value = false }
}
function date(value) {
  const timestamp = new Date(value)
  return value && !Number.isNaN(timestamp.getTime()) ? new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium', timeStyle: 'short' }).format(timestamp) : '—'
}
</script>

<template>
  <div class="vga-page game-management">
    <PageHeader :title="text('title')" />
    <Toolbar>
      <FormField :label="text('search')" for="game-search">
        <Input id="game-search" v-model="query" type="search" :placeholder="text('searchPlaceholder')" data-testid="game-search" />
      </FormField>
      <FormField :label="text('type')" data-testid="game-type">
        <Select v-model="game_type" :options="type_options" :aria-label="text('type')" data-test/>
      </FormField>
      <FormField :label="text('status')" data-testid="game-status">
        <Select v-model="listing" :options="status_options" :aria-label="text('status')" data-test/>
      </FormField>
      <template #actions>
        <Button variant="ghost" @click="reset">{{ text('reset') }}</Button>
        <Button variant="secondary" :loading="loading" :disabled="saving" @click="load">{{ text('refresh') }}</Button>
      </template>
    </Toolbar>
    <div v-if="loading" class="vga-state" role="status"><Spinner />{{ t('visualGameAdmin.common.loading') }}</div>
    <div v-else-if="error" class="vga-state" role="alert"><p class="vga-error">{{ error }}</p><Button @click="load">{{ t('visualGameAdmin.common.retry') }}</Button></div>
    <Section v-else :padded="false">
      <EmptyState v-if="!filtered.length" :title="text('empty')" :description="text('emptyHint')" />
      <Table v-else :rows="rows" :columns="columns" row-key="key" data-testid="games-table">
        <template #title="{ row }"><div class="game-management__identity"><strong>{{ row.title }}</strong><code>{{ row.id }}</code></div></template>
        <template #game_type="{ row }">{{ t(`gameCatalog.types.${row.game_type}`) }}</template>
        <template #listed="{ row }"><Badge :variant="row.listed ? 'success' : 'neutral'">{{ text(row.listed ? 'listed' : 'unlisted') }}</Badge></template>
        <template #updated_at="{ row }">{{ date(row.updated_at) }}</template>
        <template #actions="{ row }">
          <div class="game-management__actions">
            <Button v-if="row.listed || !row.blocked" size="sm" :variant="row.listed ? 'danger' : 'primary'" :disabled="saving" :data-testid="`game-listing-${row.key}`" @click="ask(row)">{{ text(row.listed ? 'unlist' : 'list') }}</Button>
            <span v-else class="game-management__reason">{{ text(row.blocked) }}</span>
          </div>
        </template>
      </Table>
      <Pagination :has-prev="page > 1" :has-next="page < pages" :summary="text('summary', { total: filtered.length, page, pages })" :prev-label="text('prev')" :next-label="text('next')" @prev="page -= 1" @next="page += 1" />
    </Section>
    <Modal :open="Boolean(target)" :title="text(target?.listed ? 'unlist' : 'list')" :close-on-backdrop="!saving" width="480px" @update:open="close">
      <template v-if="target"><p>{{ text(target.listed ? 'confirmUnlist' : 'confirmList', { title: target.title }) }}</p><p v-if="action_error" class="vga-error" role="alert">{{ action_error }}</p></template>
      <template #footer><Button variant="ghost" :disabled="saving" @click="close">{{ t('visualGameAdmin.common.cancel') }}</Button><Button :variant="target?.listed ? 'danger' : 'primary'" :loading="saving" @click="save">{{ text(target?.listed ? 'unlist' : 'list') }}</Button></template>
    </Modal>
  </div>
</template>

<style scoped>
.game-management__identity { display: grid; gap: var(--drama-space-xs); }
.game-management__identity code { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); overflow-wrap: anywhere; }
.game-management__actions { display: flex; justify-content: flex-end; }
.game-management__reason { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
</style>
